import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { CurecallInputLabel, CurecallToggle } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useId, useState } from 'react'
import { TChatbotGeneralFormValues } from '../chatbot-form.type'
import { formatCombinedIsoDuration, parseCombinedIsoDuration } from 'src/utils/duration-iso.util'
import {
  DurationInputFields,
  type TDelayState,
} from 'src/components/duration-input-fields/duration-input-fields.component'

const DEFAULT_SMS_DELAY: TDelayState = { days: 0, hours: 1, minutes: 0 }

export function ChatbotSmsDeliveryField() {
  const { t } = useTranslation()
  const id = useId()

  const {
    formState: { errors },
    control,
    setValue,
    clearErrors,
  } = useFormContext<TChatbotGeneralFormValues>()

  const smsNotReceivedDelay = useWatch({
    control,
    name: 'options.requestHumanReview.unreceivedSmsDelay',
  })

  const [delay, setDelay] = useState<TDelayState>(() => {
    if (smsNotReceivedDelay) {
      const parsed = parseCombinedIsoDuration(smsNotReceivedDelay)
      return { days: parsed.days, hours: parsed.hours, minutes: parsed.minutes }
    }
    return DEFAULT_SMS_DELAY
  })

  const updateDelay = (newDelay: TDelayState) => {
    setDelay(newDelay)
    setValue('options.requestHumanReview.unreceivedSmsDelay', formatCombinedIsoDuration(newDelay))
    clearErrors('options.requestHumanReview.unreceivedSmsDelay')
  }

  return (
    <div className="c-flex c-flex-col c-gap-4">
      <Controller
        control={control}
        name="options.requestHumanReview.unreceivedSmsDelay"
        render={({ field }) => (
          <CurecallToggle
            leftLabel={t('pages.configuration.followups.chatbot.general-form.sms-not-received-dashboard')}
            isEnabled={!!field.value}
            onChange={(enabled) => {
              if (enabled) {
                setDelay(DEFAULT_SMS_DELAY)
                setValue(
                  'options.requestHumanReview.unreceivedSmsDelay',
                  formatCombinedIsoDuration(DEFAULT_SMS_DELAY),
                )
              } else {
                setValue('options.requestHumanReview.unreceivedSmsDelay', null)
                clearErrors('options.requestHumanReview.unreceivedSmsDelay')
                setDelay(DEFAULT_SMS_DELAY)
              }
            }}
            tooltipContent={t(
              'pages.configuration.followups.chatbot.general-form.sms-not-received-dashboard-tooltip',
            )}
            tooltipPosition="right"
          />
        )}
      />

      {smsNotReceivedDelay && (
        <div className="c-flex c-flex-col c-pl-6 c-border-l-2 c-border-gray-200 c-mb-4">
          <CurecallInputLabel
            id={id}
            label={t('pages.configuration.followups.chatbot.general-form.sms-not-received-delay')}
            isRequired={true}
            tooltipContent={t(
              'pages.configuration.followups.chatbot.general-form.sms-not-received-delay-tooltip',
            )}
            tooltipPosition="right"
          />
          <Controller
            control={control}
            name="options.requestHumanReview.unreceivedSmsDelay"
            rules={{
              validate: (value) => {
                if (!value) return true
                const { days, hours, minutes } = parseCombinedIsoDuration(value)
                return days * 24 * 60 + hours * 60 + minutes >= 5
              },
            }}
            render={() => (
              <div className="c-flex c-flex-col c-gap-2">
                <DurationInputFields delay={delay} onDelayChange={updateDelay} />
                {errors.options?.requestHumanReview?.unreceivedSmsDelay && (
                  <span className="c-text-sm c-text-danger-600">
                    {t('pages.configuration.followups.chatbot.general-form.sms-not-received-delay-error')}
                  </span>
                )}
              </div>
            )}
          />
        </div>
      )}
    </div>
  )
}
